/**
 * Shared Tooltip System
 *
 * Single floating element, dark glass aesthetic.
 * Reused across all interaction systems — Validate, Sense, modes.
 *
 * Uses a WeakMap to track attached elements so that re-attaching
 * to the same element (e.g., readinessEl on re-render) correctly
 * detaches the old handler before wiring the new one.
 */

let tooltipEl: HTMLElement | null = null;
let tooltipTimeout: ReturnType<typeof setTimeout> | null = null;

/** Track attached elements to support safe re-attachment */
const attached = new WeakMap<HTMLElement, { enter: () => void; leave: () => void }>();

function ensureTooltip(): HTMLElement {
  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.className = 'aura-tooltip';
    document.body.appendChild(tooltipEl);
  }
  return tooltipEl;
}

export function showTooltip(el: HTMLElement, text: string): void {
  const tip = ensureTooltip();
  if (tooltipTimeout) clearTimeout(tooltipTimeout);

  tip.textContent = text;

  // Position offscreen first to measure
  tip.style.left = '-9999px';
  tip.style.top = '-9999px';
  tip.classList.add('visible');

  // Force layout to get actual tooltip dimensions
  const tipRect = tip.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();

  // Center horizontally above the element
  let left = elRect.left + elRect.width / 2 - tipRect.width / 2;
  let top = elRect.top - tipRect.height - 6;

  // Clamp horizontal to viewport
  left = Math.max(8, Math.min(left, window.innerWidth - tipRect.width - 8));

  // If above viewport, show below instead
  if (top < 8) {
    top = elRect.bottom + 6;
  }

  tip.style.left = `${left}px`;
  tip.style.top = `${top}px`;
}

export function hideTooltip(): void {
  if (tooltipTimeout) clearTimeout(tooltipTimeout);
  tooltipTimeout = null;
  tooltipEl?.classList.remove('visible');
}

/**
 * Attach a hover tooltip to any element.
 * Safe to call multiple times on the same element — detaches old handler first.
 */
export function attachTooltip(el: HTMLElement, text: string): void {
  // Detach previous handler if re-attaching to the same element
  const existing = attached.get(el);
  if (existing) {
    el.removeEventListener('mouseenter', existing.enter);
    el.removeEventListener('mouseleave', existing.leave);
  }

  const enter = () => {
    // Small delay to prevent flicker on fast mouse movement
    tooltipTimeout = setTimeout(() => showTooltip(el, text), 280);
  };
  const leave = () => hideTooltip();

  el.addEventListener('mouseenter', enter);
  el.addEventListener('mouseleave', leave);
  attached.set(el, { enter, leave });
}
